import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Avatar,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import { useAuth } from './AuthContext';
import { CalendarMonth as BookingIcon, ContentCut as ServicesIcon } from '@mui/icons-material';
import { usePermissions } from './PermissionsContext';
import ReminderPanel from './ReminderPanel';

// Icons
import {
  Calculate as CalculateIcon,
  Inventory as StockIcon,
  People as TeamIcon,
  Schedule as TimeIcon,
  Dashboard as OperationsIcon,
  Assessment as ReportsIcon,
  Store as RentalIcon,
  MenuBook as KnowledgeIcon,
  SupportAgent as SupportIcon,
  Description as DocumentsIcon,
  CloudUpload as DataIcon,
  LockOpen as LockOpenIcon,
  ArrowForward as ArrowIcon,
  EventNote as DiaryIcon
} from '@mui/icons-material';

const HubDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  // Custom Pound Icon Component
  const PoundIcon = ({ sx }) => (
    <Box sx={{ fontSize: 32, fontWeight: 'bold', ...sx }}>£</Box>
  );

  // 8 CORE SYSTEMS - Internal navigation to your platform
  const coreSystems = [
    {
      id: 'calculator',
      title: 'Pricing Calculator',
      description: 'Professional pricing strategies, service costing, and profit margin calculations.',
      icon: <CalculateIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #667eea, #764ba2)',
      path: '/calculator',
      color: '#667eea',
      badge: 'Pro'
    },
    {
      id: 'stock',
      title: 'Stock Management',
      description: 'Product inventory, suppliers, purchase orders, and stock tracking.',
      icon: <StockIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #E91E63, #C2185B)',
      path: '/stock',
      color: '#E91E63',
      badge: null
    },
    {
      id: 'money',
      title: 'Money & Finance',
      description: 'Sales tracking, financial reports, VAT, commissions, and business analytics.',
      icon: <PoundIcon />,
      iconBg: 'linear-gradient(135deg, #4CAF50, #45a049)',
      path: '/money',
      color: '#4CAF50',
      badge: null
    },
    {
      id: 'team',
      title: 'Team Management',
      description: 'Employee records, reviews, training, disciplinary, and recruitment.',
      icon: <TeamIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
      path: '/team',
      color: '#9C27B0',
      badge: null
    },
    {
      id: 'time',
      title: 'Time & Attendance',
      description: 'Holiday calendar, absence tracking, approvals, and workforce scheduling.',
      icon: <TimeIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #00bcd4, #0097a7)',
      path: '/time',
      color: '#00bcd4',
      badge: null
    },
    {
      id: 'operations',
      title: 'Operations Dashboard',
      description: 'Daily operations, KPIs, forecasting, and business performance metrics.',
      icon: <OperationsIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #2196F3, #1976D2)',
      path: '/home',
      color: '#2196F3',
      badge: null
    },
    {
      id: 'reports',
      title: 'Business Reports',
      description: 'Comprehensive reports, tax calculations, profit & loss, and analytics.',
      icon: <ReportsIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #FF9800, #F57C00)',
      path: '/reports',
      color: '#FF9800',
      badge: null
    },
    {
      id: 'rental',
      title: 'Rental Management',
      description: 'Chair rentals, space management, renter tracking, and rental agreements.',
      icon: <RentalIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #FF5722, #E64A19)',
      path: '/rental_dashboard',
      color: '#FF5722',
      badge: null
    },
    {
      id: 'services',
      title: 'Services & Packages',
      description: 'Manage your service menu, durations, pricing, packages and rebook intervals.',
      icon: <ServicesIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #00ACC1, #00838F)',
      path: '/pro/services-hub',   // ← was '/pro/services'
      color: '#00ACC1',
      badge: 'Pro',
    },
    {
      id: 'online_booking',
      title: 'Online Booking',
      description: 'Manage stylist availability, service assignments, and your public booking page.',
      icon: <BookingIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #00897B, #00695C)',
      path: '/pro/booking-settings',
      color: '#00897B',
      badge: 'Pro',
      requiresPermission: 'online_booking',
    },
    {
      id: 'pro_diary',
      title: 'Diary',
      description: 'See today\'s bookings by stylist, move appointments and check clients out.',
      icon: <DiaryIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #5C6BC0, #3949AB)',
      path: '/pro/diary',
      color: '#5C6BC0',
      badge: 'Pro',
      requiresPermission: 'online_booking',
    },
  ];

  // 4 ADDITIONAL RESOURCES - Mix of internal and external
  const additionalResources = [
    {
      id: 'documents',
      title: 'Document Library',
      description: 'Professional templates, contracts, policies, and client documents.',
      icon: <DocumentsIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #607D8B, #455A64)',
      path: '/documents',
      color: '#607D8B',
      badge: null,
      isExternal: false
    },
    {
      id: 'data',
      title: 'Data Management',
      description: 'Import and export data, bulk operations, and system migrations.',
      icon: <DataIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #3F51B5, #303F9F)',
      path: '/import',
      color: '#3F51B5',
      badge: null,
      isExternal: false
    },
    {
      id: 'knowledge',
      title: 'Knowledge Base',
      description: 'Guides, tutorials, and documentation for all features.',
      icon: <KnowledgeIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #795548, #5D4037)',
      url: 'https://app.salonlogic.co.uk/kb',
      color: '#795548',
      chipColor: '#5D4037',
      badge: null,
      isExternal: true
    },
    {
      id: 'support',
      title: 'Priority Support',
      description: 'Expert help with technical issues and business advice.',
      icon: <SupportIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #009688, #00796B)',
      url: 'https://salonlogicpro.co.uk/index.php/support-centre/',
      color: '#00695C',
      chipColor: '#00695C',   // darker teal, passes 4.5:1 on white
      badge: null,
      isExternal: true
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa', py: 4 }}>
      <Container maxWidth="xl">
        
        {/* Welcome Header */}
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 5,
            mb: 4,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Welcome to SalonLogic Pro
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
              Your complete business management hub
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockOpenIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Secure session active • {user?.email || 'User'}
              </Typography>
            </Box>
          </Box>
          
          {/* Decorative circles */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            right: 100,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            zIndex: 0
          }} />
        </Paper>
        <ReminderPanel />

        {/* CORE SYSTEMS Section */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5 }}>
                Your Business Tools
              </Typography>
              <Typography variant="body2" color="text.secondary">
                11 core systems to manage every aspect of your salon
              </Typography>
            </Box>
            <Chip 
              label={`${coreSystems.filter(s => !s.requiresPermission || hasPermission(s.requiresPermission)).length} Systems Active`}
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Grid container spacing={3}>
            {coreSystems.filter(s => !s.requiresPermission || hasPermission(s.requiresPermission)).map((system) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={system.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    minHeight: '320px',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      borderColor: system.color,
                      '& .action-button': {
                        background: system.iconBg
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Icon & Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          background: system.iconBg,
                          boxShadow: `0 4px 12px ${system.color}40`
                        }}
                      >
                        {system.icon}
                      </Avatar>
                      {system.badge && (
                        <Chip
                          label={system.badge}
                          size="small"
                          sx={{
                            background: system.iconBg,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        mb: 1,
                        color: '#2c3e50'
                      }}
                    >
                      {system.title}
                    </Typography>

                    {/* Description */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 3,
                        flexGrow: 1,
                        lineHeight: 1.6
                      }}
                    >
                      {system.description}
                    </Typography>

                    {/* Action Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      className="action-button"
                      endIcon={<ArrowIcon />}
                      onClick={() => navigate(system.path)}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s',
                        mt: 'auto',
                        '&:hover': {
                          background: system.iconBg,
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      Open {system.title.split(' ')[0]}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* ADDITIONAL RESOURCES Section */}
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5 }}>
              Additional Resources
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Knowledge base, community support, and professional documents
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {additionalResources.map((resource) => (
              <Grid item xs={12} sm={6} md={3} key={resource.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    minHeight: '320px',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      borderColor: resource.color,
                      '& .action-button': {
                        background: resource.iconBg
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Icon & External Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          background: resource.iconBg,
                          boxShadow: `0 4px 12px ${resource.color}40`
                        }}
                      >
                        {resource.icon}
                      </Avatar>
                      {resource.isExternal && (
                        <Chip
                          label="External"
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: resource.chipColor || resource.color,
                            color: resource.chipColor || resource.color,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        mb: 1,
                        color: '#2c3e50'
                      }}
                    >
                      {resource.title}
                    </Typography>

                    {/* Description */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 3,
                        flexGrow: 1,
                        lineHeight: 1.6
                      }}
                    >
                      {resource.description}
                    </Typography>

                    {/* Action Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      className="action-button"
                      endIcon={<ArrowIcon />}
                      onClick={() => resource.path ? navigate(resource.path) : window.open(resource.url, '_blank')}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s',
                        mt: 'auto',
                        '&:hover': {
                          background: resource.iconBg,
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      {resource.isExternal ? 'Visit Site' : `Open ${resource.title.split(' ')[0]}`}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Quick Start Guide */}
        <Paper
          elevation={0}
          sx={{
            mt: 5,
            p: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2c3e50' }}>
            💡 Quick Start Guide
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                1. Set Up Your Pricing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Use the Pricing Calculator to configure your services and profit margins
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                2. Add Your Team
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Import or add employees in Team Management for scheduling and payroll
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                3. Track Your Money
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Start recording sales and expenses to unlock powerful analytics
              </Typography>
            </Grid>
          </Grid>
        </Paper>

      </Container>
    </Box>
  );
};

export default HubDashboard;